<template>
    <div v-if="loadingVersions" class="d-flex version-loader">
        <b-spinner class="loader-spinner mr-3" />
        <p class="text">Loading versions...</p>
    </div>
    <div v-else class="version-restore">
        <div class="info-message mb-3">
            <span class="info-icon">
                <font-awesome-icon :icon="['fa-solid', 'fa-circle-info']" />
            </span>
            <span class="info-text">Select a previous version of your template to create a restored copy.</span>
        </div>

        <div class="mb-3">
            <div class="template-name">{{ templateName }}</div>
            <div class="template-id">{{ templateId }}</div>
        </div>
        <select v-model="version" class="custom-select" name="versions-select">
            <option value="" selected>-- Select version --</option>
            <option
                v-for="({ VersionId, LastModified }, index) in versions"
                :key="`dropdown-${index}`"
                :value="VersionId"
            >
                {{ LastModified.replace('T', ' ') }}
            </option>
        </select>

        <div v-show="errorMessage" class="error-message w-100 pb-3 text-center">
            {{ errorMessage }}
        </div>

        <div class="modal-footer row pb-0">
            <div class="btn-back col-xs-6 text-left">
                <b-button variant="outline-dark" :disabled="saving" @click="goBack()"> Back </b-button>
            </div>
            <div class="btn-restore col-xs-6 text-right">
                <loader-btn
                    variant="primary"
                    type="submit"
                    loading-text="Restoring"
                    :loading="saving"
                    :disabled="saving || version == ''"
                    @click="restoreTemplateVersion()"
                >
                    Restore
                </loader-btn>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';
import { ProductVersionRestoredInfo, ProductVersions } from '@/api/typings/product';
import { getProductVersions, createProductFromVersion } from '@/api/product';
import { AxiosError } from 'axios';

const props = defineProps<{
    templateId: string;
    templateName: string;
}>();

const version = ref('');
const saving = ref(false);
const loadingVersions = ref(false);
const errorMessage = ref('');
const newTemplateId = ref('');
const newTemplateName = ref('');
const versions = ref<ProductVersions[]>([]);

const emit = defineEmits<{
    (event: 'go-back'): void;
    (event: 'template-version-restored', template: ProductVersionRestoredInfo): void;
}>();

const getTemplateVersions = async () => {
    try {
        loadingVersions.value = true;
        versions.value = await getProductVersions(props.templateId);
    } finally {
        loadingVersions.value = false;
    }
};

const goBack = () => {
    emit('go-back');
};

const restoreTemplateVersion = async () => {
    try {
        saving.value = true;
        const versionDate = versions.value.find((v) => v.VersionId == version.value)?.LastModified;
        if (versionDate) {
            newTemplateName.value = `${props.templateName} - Restored version: ${versionDate
                .toLocaleString('en-US', { timeZone: 'UTC' })
                .replace('T', ' ')}`;
            newTemplateId.value = await createProductFromVersion(
                props.templateId,
                version.value,
                newTemplateName.value
            );
            emit('template-version-restored', { templateId: newTemplateId.value, templateName: props.templateName });
        }
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.request?.status !== 404) {
            window.rumReportError(axiosError);
            errorMessage.value = `Error restoring product ${props.templateName} version ${
                axiosError.response?.data?.Message ?? ''
            }`;
        }
    } finally {
        saving.value = false;
    }
};

onMounted(async () => {
    await getTemplateVersions();
});
</script>
<style lang="scss" scoped>
.info-message {
    display: flex;
    align-items: center;
    gap: 10px;
    background-color: #f5f6f8;
    border-radius: 8px;
    border: 1px solid #e1e3e6;
    padding: 12px 16px;
    font-size: 14px;
}
.info-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #333;
    i {
        font-size: 16px;
    }
}
.info-text {
    line-height: 1.4;
}

.version-loader {
    .text {
        font-size: 1.2em;
        margin: auto 0;
    }
}

.version-restore {
    .modal-footer {
        margin-top: 25px;
        border-top: 1px solid #dee2e6;
        padding-left: 15px !important;
        padding-right: 15px !important;
    }

    .btn-back,
    .btn-restore {
        padding-left: 0 !important;
        padding-right: 0 !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
    }
}

.template-name {
    color: var(--Gray-scale-gray-darkest, #333);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    margin-bottom: 5px;
}

.template-id {
    color: var(--Primary-primary, #7258e0);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}
</style>
